import React, { useMemo, useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react'

import { EuiPanel, EuiTitle, EuiText, EuiInMemoryTable, EuiBasicTableColumn, SortDirection, EuiSpacer, EuiFlexGroup, EuiFlexItem, EuiLink, EuiButtonIcon } from '@elastic/eui'

import { AIPlan, AIPlanCustomSection, AIPlanCampaignBuilderType, AIPlanStub } from 'api/interfaces/AIPlan'
import { useGetDemographicAndHousingCharacteristicsQuery, useGetEducationalAttainmentQuery, useGetFinancialCharacteristicsQuery } from 'api/rtkQueryApi/platform/censusDataApi'
import { MapBoxContainer, ZipCodeBoundaryStyle } from 'components/mapBox/MapBoxContainer'
import { useMapBox } from 'components/mapBox/useMapBox'
import { fitToBounds } from 'features/planner/pages/MasterCampaignPlanPage/components/Projections/camera'
import { calculateTotal } from 'utils/CampaignMathUtils'
import { formatPercentage } from 'utils/Formatters'
import { buildMapboxCaseExpression } from 'utils/allocationColors'
import { calculateCenterPoint, calculateOptimalZoom } from 'utils/mapUtils'

import { useAIPlanHouseholdZipCodes } from './useAIPlanProjectionsUtils'

type GeoJSONFeature = GeoJSON.Feature<GeoJSON.Point, { allocation: number }>

export interface AIPlanTargetedHouseholdLocationsProps {
  aiPlan: AIPlan
  stub: AIPlanStub
  isCapturing?: boolean
  section?: AIPlanCustomSection
  editMode?: boolean
  onEdit?: () => void
}

export interface AIPlanTargetedHouseholdLocationsRef {
  getMapCanvas: () => Promise<string | null>
  isMapReady: () => boolean
}

interface HouseholdData {
  city: string
  zipCode: string
  medianIncome: string
  age2554: string
  collegeEducated: string
  allocation: number
}

const AIPlanTargetedHouseholdLocations = forwardRef<AIPlanTargetedHouseholdLocationsRef, AIPlanTargetedHouseholdLocationsProps>(({ aiPlan, stub, isCapturing, section, editMode, onEdit }, ref) => {
  const householdCampaigns = useMemo(() => {
    if (!stub?.campaignBuilders) {
      return []
    }
    return stub.campaignBuilders.filter(campaign => {
      const isInternet = campaign.campaignType === AIPlanCampaignBuilderType.Internet
      const isTV = campaign.campaignType === AIPlanCampaignBuilderType.TV
      const isOffAdCritterWithProjectionsAndGeoHeatmap = campaign.campaignType === AIPlanCampaignBuilderType.OffAdCritter && campaign.includeInProjections && campaign.includeInGeoHeatmap

      return isInternet || isTV || isOffAdCritterWithProjectionsAndGeoHeatmap
    })
  }, [stub])

  const zipCodes = useAIPlanHouseholdZipCodes(stub)

  const { data: demographicData } = useGetDemographicAndHousingCharacteristicsQuery({ zipCodes }, { skip: zipCodes.length === 0 })
  const { data: educationData } = useGetEducationalAttainmentQuery({ zipCodes }, { skip: zipCodes.length === 0 })
  const { data: financialData } = useGetFinancialCharacteristicsQuery({ zipCodes }, { skip: zipCodes.length === 0 })

  const getCityFromZipCode = (zipCode: string): string => {
    if (!householdCampaigns) {
      return 'Unknown'
    }

    for (const campaign of householdCampaigns) {
      if (!campaign.zips) {
        continue
      }

      const zipData = campaign.zips.find(zip => zip.code === zipCode)
      if (!zipData?.name) {
        continue
      }

      const nameParts = zipData.name.split(',')
      return nameParts.length > 1 ? nameParts[1].trim() : nameParts[0].trim()
    }
    return 'Unknown'
  }

  const transformCensusDataToHousehold = (zipCode: string, allocation: number): HouseholdData | null => {
    const demographic = demographicData?.[zipCode]
    const education = educationData?.[zipCode]
    const financial = financialData?.[zipCode]

    if (!demographic || !education || !financial) {
      return null
    }

    const medianIncome = financial.medianHouseholdIncome && financial.medianHouseholdIncome > 0 ? `$${financial.medianHouseholdIncome.toLocaleString()}` : 'N/A'

    const ages25to54 = demographic.ages25To34 + demographic.ages35To44 + demographic.ages45To54
    const totalPopulation = demographic.totalPopulation
    const age2554Percentage = totalPopulation > 0 ? `${Math.round((ages25to54 / totalPopulation) * 100)}%` : 'N/A'

    const collegeEducatedPercentage = education.age25AndOver > 0 ? `${Math.round((education.age25AndOverBachelorsDegreeOrHigher / education.age25AndOver) * 100)}%` : 'N/A'

    return {
      city: getCityFromZipCode(zipCode),
      zipCode,
      medianIncome,
      age2554: age2554Percentage,
      collegeEducated: collegeEducatedPercentage,
      allocation
    }
  }

  const householdData = useMemo(() => {
    if (!householdCampaigns || !demographicData || !educationData || !financialData || zipCodes.length === 0) {
      return []
    }

    const campaignPopulationMap = new Map<string, number>()
    householdCampaigns.forEach(campaign => {
      let totalPopulation = 0
      campaign.zips?.forEach(zip => {
        totalPopulation += demographicData[zip.code]?.totalPopulation || 0
      })
      campaignPopulationMap.set(campaign.id, totalPopulation)
    })

    const zipDollarsMap = new Map<string, number>()
    householdCampaigns.forEach(campaign => {
      const campaignBudget = calculateTotal(campaign.intervalBudget, campaign.intervalDuration)
      const campaignTotalPopulation = campaignPopulationMap.get(campaign.id) || 0

      if (campaignTotalPopulation === 0) {
        return
      }

      const ratePerPerson = campaignBudget / campaignTotalPopulation

      campaign.zips?.forEach(zip => {
        const zipPopulation = demographicData[zip.code]?.totalPopulation || 0
        const dollarsForZip = ratePerPerson * zipPopulation

        const currentDollars = zipDollarsMap.get(zip.code) || 0
        zipDollarsMap.set(zip.code, currentDollars + dollarsForZip)
      })
    })

    const totalHouseholdBudget = householdCampaigns.reduce((sum, campaign) => {
      return sum + calculateTotal(campaign.intervalBudget, campaign.intervalDuration)
    }, 0)

    return zipCodes
      .map(zipCode => {
        const totalDollarsForZip = zipDollarsMap.get(zipCode) || 0
        const allocation = totalHouseholdBudget > 0 ? totalDollarsForZip / totalHouseholdBudget : 0

        return transformCensusDataToHousehold(zipCode, allocation)
      })
      .filter((item): item is HouseholdData => item !== null)
  }, [demographicData, educationData, financialData, zipCodes, householdCampaigns])

  const allocationMap = useMemo(() => {
    if (!householdData || householdData.length === 0) {
      return new Map<string, number>()
    }

    return new Map(householdData.map(h => [h.zipCode, h.allocation]))
  }, [householdData])

  const [geographicFeatures, setGeographicFeatures] = useState<GeoJSONFeature[]>([])

  const { mapContainerRef, initializeMap, updateTilesetLayer, setInitialCenterAndZoom, returnToCenter, searchPlace, hasInitialCenter, isReady, isStyleLoaded, isLoading: isMapLoading, hasError: hasMapError, error: mapError, addPointLayer, fitToZipCodeBounds, mapInstance, extractMapCanvas, hideNavigationControls, showNavigationControls } = useMapBox()

  useEffect(() => {
    if (isReady) {
      if (isCapturing) {
        hideNavigationControls()
      } else {
        showNavigationControls()
      }
    }
  }, [isCapturing, isReady, hideNavigationControls, showNavigationControls])

  useImperativeHandle(
    ref,
    () => ({
      getMapCanvas: async () => {
        return await extractMapCanvas()
      },
      isMapReady: () => {
        return isStyleLoaded && isReady
      }
    }),
    [extractMapCanvas, isStyleLoaded, isReady]
  )

  const handleViewZipFromGrid = useCallback(
    async (zipCode: string) => {
      try {
        const success = await fitToZipCodeBounds(zipCode)

        if (!success) {
          const placeResponse = await searchPlace(zipCode)
          if (placeResponse?.features?.length > 0) {
            const feature = placeResponse.features[0]
            const bounds: [[number, number], [number, number]] = [
              [feature.geometry.coordinates[0] - 0.05, feature.geometry.coordinates[1] - 0.05],
              [feature.geometry.coordinates[0] + 0.05, feature.geometry.coordinates[1] + 0.05]
            ]
            fitToBounds(mapInstance.current, bounds, 100)
          }
        }
      } catch (error) {}
    },
    [fitToZipCodeBounds, searchPlace, mapInstance]
  )

  useEffect(() => {
    if (!isReady || allocationMap.size === 0) {
      return
    }

    updateTilesetLayer(allocationMap)

    if (householdData.length > 0) {
      const setCenter = async () => {
        const mapBoxPlaceResponses = await Promise.all(
          householdData.map(async h => {
            return await searchPlace(h.zipCode)
          })
        )

        const geoPoints = mapBoxPlaceResponses
          .map(response => {
            if (response.features && response.features.length > 0) {
              const feature = response.features[0]
              return {
                lat: feature.geometry.coordinates[1],
                lng: feature.geometry.coordinates[0]
              }
            }
            return null
          })
          .filter((p): p is { lat: number; lng: number } => p !== null)

        const features: GeoJSONFeature[] = mapBoxPlaceResponses
          .map((response, index) => {
            if (response.features && response.features.length > 0 && householdData[index]) {
              const feature = response.features[0]
              return {
                type: 'Feature' as const,
                geometry: {
                  type: 'Point' as const,
                  coordinates: [feature.geometry.coordinates[0], feature.geometry.coordinates[1]]
                },
                properties: {
                  allocation: householdData[index].allocation
                }
              }
            }
            return null
          })
          .filter((f): f is GeoJSONFeature => f !== null)

        setGeographicFeatures(features)

        const center = calculateCenterPoint(geoPoints)
        const zoom = calculateOptimalZoom(geoPoints)

        if (center) {
          setInitialCenterAndZoom([center.lng, center.lat], zoom)
        }
      }

      setCenter()
    }
  }, [isReady, allocationMap, updateTilesetLayer, setInitialCenterAndZoom, searchPlace, householdData])

  useEffect(() => {
    if (!isReady || geographicFeatures.length === 0) {
      return
    }

    const featureCollection: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: geographicFeatures
    }

    addPointLayer('household-clusters', featureCollection, {
      clusterSettings: {
        cluster: true,
        maxzoom: 8,
        radius: 100,
        properties: {
          allocation_sum: ['+', ['get', 'allocation']]
        }
      },
      clusterOptions: {
        type: 'circle',
        filter: ['has', 'point_count'],
        maxzoom: 8,
        paint: {
          'circle-color': buildMapboxCaseExpression('allocation_sum'),
          'circle-radius': ['step', ['get', 'point_count'], 15, 1, 25, 5, 30, 10, 35],
          'circle-opacity': 0.7
        }
      },
      unclusteredOptions: {
        type: 'circle',
        maxzoom: 8,
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': buildMapboxCaseExpression('allocation'),
          'circle-radius': 15,
          'circle-opacity': 0.7
        }
      }
    })
  }, [isReady, geographicFeatures, addPointLayer])

  const pagination = {
    initialPageSize: 10,
    pageSizeOptions: [5, 10, 20]
  }

  const householdSorting = {
    allowNeutralSort: true,
    enableAllColumns: true,
    sort: {
      field: 'allocation',
      direction: SortDirection.DESC
    }
  }

  const householdColumns: Array<EuiBasicTableColumn<HouseholdData>> = [
    ...(!isCapturing
      ? [
          {
            field: 'location',
            name: 'Location',
            sortable: false,
            render: (value: any, household: HouseholdData) => (
              <EuiLink onClick={() => handleViewZipFromGrid(household.zipCode)} aria-label={`View ${household.zipCode} on map`}>
                View on Map
              </EuiLink>
            )
          } as EuiBasicTableColumn<HouseholdData>
        ]
      : []),
    {
      field: 'zipCode',
      name: 'Zip Code',
      sortable: true
    },
    {
      field: 'city',
      name: 'City',
      sortable: true
    },
    {
      field: 'medianIncome',
      name: 'Median Income',
      sortable: true
    },
    {
      field: 'age2554',
      name: 'Age 25-54',
      sortable: true
    },
    {
      field: 'collegeEducated',
      name: 'College Educated',
      sortable: true
    },
    {
      field: 'allocation',
      name: 'Allocation',
      sortable: (item: HouseholdData) => item.allocation,
      render: (allocation: number) => formatPercentage(allocation, 1, 1)
    }
  ]

  if (section?.hideAll === true) {
    return null
  }

  const sectionTitle = section?.title ?? 'Geographic Distribution'
  const sectionContent = section?.content ?? 'Household-level targeting allows for hyper-localized ad delivery. The map and table below show projected ad distribution across targeted zip codes, with allocation proportional to population density within your audience criteria.'
  const showTitle = section?.showTitle ?? true

  return (
    <EuiPanel paddingSize='l'>
      {!isCapturing && (
        <>
          <EuiFlexGroup alignItems='center' gutterSize='s'>
            {showTitle && (
              <EuiFlexItem grow={false}>
                <EuiTitle size='m'>
                  <h2>{sectionTitle}</h2>
                </EuiTitle>
              </EuiFlexItem>
            )}
            {editMode && onEdit && (
              <EuiFlexItem grow={false}>
                <EuiButtonIcon iconType='pencil' style={{ color: '#0071c2' }} onClick={onEdit} aria-label='Edit Geographic Distribution section' />
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
          <EuiSpacer size='s' />
          <EuiText size='m' color='subdued' style={{ whiteSpace: 'pre-wrap' }}>
            {sectionContent}
          </EuiText>
          <EuiSpacer size='l' />
        </>
      )}

      {householdData.length > 0 ? (
        <EuiInMemoryTable items={householdData} columns={householdColumns} pagination={pagination} sorting={householdSorting} responsive={true} />
      ) : (
        zipCodes.length > 0 && (
          <EuiText textAlign='center' color='subdued'>
            <p>No demographic data available for the targeted zip codes.</p>
          </EuiText>
        )
      )}

      {householdData.length > 0 && (
        <>
          <EuiSpacer size='l' />

          <div style={isCapturing ? { width: 2000, height: 500, marginLeft: 'auto', marginRight: 'auto' } : { maxWidth: 1000, height: 400, marginLeft: 'auto', marginRight: 'auto', marginBottom: '2rem' }}>
            {!isCapturing && (
              <>
                <EuiFlexGroup justifyContent='spaceBetween' gutterSize='s'>
                  <EuiFlexItem grow={false}>
                    <EuiLink color='success' onClick={returnToCenter} disabled={!hasInitialCenter} aria-label='Show all geographic areas'>
                      Show All
                    </EuiLink>
                  </EuiFlexItem>
                  <EuiFlexItem grow={false}>
                    <EuiLink color='success' onClick={returnToCenter} disabled={!hasInitialCenter} aria-label='Reset map view'>
                      Reset View
                    </EuiLink>
                  </EuiFlexItem>
                </EuiFlexGroup>
                <EuiSpacer size='s' />
              </>
            )}
            <MapBoxContainer mapContainerRef={mapContainerRef} initializeMap={initializeMap} styleUrl={ZipCodeBoundaryStyle} isReady={isReady} isLoading={isMapLoading} hasError={hasMapError} error={mapError} />
          </div>
        </>
      )}
    </EuiPanel>
  )
})

AIPlanTargetedHouseholdLocations.displayName = 'AIPlanTargetedHouseholdLocations'

export default AIPlanTargetedHouseholdLocations
